import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, CheckCircle } from 'lucide-react';
import api from '../lib/api';

interface NpsSurvey {
  id: string;
  title: string;
  questions: { id: string; question: string; type: string }[];
}

interface Props {
  survey: NpsSurvey;
  visitId: string;
  storeId: string;
  onClose: () => void;
}

export default function NpsBottomSheet({ survey, visitId, storeId, onClose }: Props) {
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const npsQuestion = survey.questions.find(q => q.type === 'SCALE');
  const textQuestion = survey.questions.find(q => q.type === 'TEXT');

  const submitMutation = useMutation({
    mutationFn: async () => {
      const answers: Record<string, string> = {};
      if (npsQuestion && score !== null) answers[npsQuestion.id] = String(score);
      if (textQuestion && comment.trim()) answers[textQuestion.id] = comment.trim();

      await api.post('/surveys/respond', {
        survey_id: survey.id,
        visit_id: visitId,
        store_id: storeId,
        answers,
      });
    },
    onSuccess: () => {
      setSubmitted(true);
      setTimeout(onClose, 1500);
    },
  });

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-end">
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative w-full bg-white rounded-t-2xl p-6 pb-8 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
          <p className="font-semibold text-gray-900">Obrigado!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full bg-white rounded-t-2xl p-6 pb-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400">
          <X className="h-5 w-5" />
        </button>

        <h3 className="font-semibold text-gray-900 mb-1">{survey.title || 'Avaliacao da Visita'}</h3>
        <p className="text-sm text-gray-500 mb-4">
          {npsQuestion?.question || 'De 0 a 10, como voce avalia esta visita?'}
        </p>

        <div className="flex items-center justify-center gap-1 mb-2">
          {Array.from({ length: 11 }, (_, i) => i).map(value => (
            <button
              key={value}
              onClick={() => setScore(value)}
              className={`w-9 h-9 rounded-lg border-2 font-bold text-sm transition-colors ${
                score === value
                  ? value >= 9 ? 'border-green-500 bg-green-500 text-white'
                    : value >= 7 ? 'border-yellow-500 bg-yellow-500 text-white'
                    : 'border-red-500 bg-red-500 text-white'
                  : 'border-gray-200 text-gray-600 active:bg-gray-50'
              }`}
            >
              {value}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-400 mb-4">
          <span>Nada provavel</span>
          <span>Muito provavel</span>
        </div>

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Comentario (opcional)"
          rows={2}
          className="w-full border border-gray-200 rounded-lg p-3 text-sm resize-none mb-4"
        />

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 text-gray-500 font-medium rounded-xl border border-gray-200"
          >
            Pular
          </button>
          <button
            onClick={() => submitMutation.mutate()}
            disabled={score === null || submitMutation.isPending}
            className="flex-1 py-3 bg-primary text-white font-semibold rounded-xl disabled:opacity-50"
          >
            {submitMutation.isPending ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}
